import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import prairiSettingIcon from '../assets/prari setting.png';
import prairiLogo from '../assets/prairiwhite.png';

const STARTUP_REASONS = [
  { value: 'found-funding-on-prairi', label: 'We just received our funding on PRAIRI' },
  { value: 'found-funding-not-on-prairi', label: 'We received our funding (but not on PRAIRI)' },
  {
    value: 'not-pursuing-investment',
    label: "We've decided not to pursue investment at this time",
  },
  { value: 'other', label: 'Other reason:' },
];

const INVESTOR_REASONS = [
  { value: 'too-many-notifications', label: 'Getting too many notifications' },
  { value: 'not-enough-quality-matches', label: 'Not enough quality matches' },
  { value: 'too-many-matches', label: 'Too many matches' },
  { value: 'other', label: 'Other reason:' },
];

const REDIRECT_COUNTDOWN_SECONDS = 5;

interface PauseProfilePageProps {
  onDone?: () => void;
}

const PauseProfilePage: React.FC<PauseProfilePageProps> = ({ onDone }) => {
  const { user, firebaseUser, signOut } = useAuth();
  const { startupProfile, investorProfile, loadProfile } = useProfile();
  const navigate = useNavigate();
  const [reason, setReason] = useState<string>('');
  const [otherReason, setOtherReason] = useState<string>('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isPaused, setIsPaused] = useState<boolean | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeletionSuccess, setShowDeletionSuccess] = useState(false);
  const [deletionCountdown, setDeletionCountdown] = useState(REDIRECT_COUNTDOWN_SECONDS);

  const done = () => (onDone ? onDone() : navigate('/settings'));

  useEffect(() => {
    if (!onDone) window.scrollTo(0, 0);
    // Get current pause status
    if (user?.userType === 'startup' && startupProfile) {
      setIsPaused(startupProfile.paused ?? false);
    } else if (user?.userType === 'investor' && investorProfile) {
      setIsPaused(investorProfile.paused ?? false);
    } else {
      setIsPaused(false);
    }
  }, [user, startupProfile, investorProfile]);

  useEffect(() => {
    if (!showDeletionSuccess || deletionCountdown <= 0) return;
    const t = setInterval(() => {
      setDeletionCountdown(prev => {
        if (prev <= 1) {
          clearInterval(t);
          signOut().then(() => navigate('/', { replace: true }));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [showDeletionSuccess, deletionCountdown, signOut, navigate]);

  const reasons = user?.userType === 'startup' ? STARTUP_REASONS : INVESTOR_REASONS;

  const handlePause = async () => {
    if (!reason) {
      setShowErrorMessage(true);
      return;
    }

    if (reason === 'other' && !otherReason.trim()) {
      setShowErrorMessage(true);
      return;
    }

    setLoading(true);
    setShowErrorMessage(false);

    try {
      const functions = getFunctions();
      const pauseProfileFunction = httpsCallable(functions, 'pauseProfile');

      const finalReason = reason === 'other' ? otherReason : reason;

      const result = await pauseProfileFunction({
        userId: firebaseUser?.uid,
        userType: user?.userType,
        paused: true,
        pauseReason: finalReason,
      });

      const data = result.data as { success: boolean; error?: string };

      if (data.success) {
        setIsPaused(true);
        setShowSuccessModal(true);
        // Refresh profile
        if (user && firebaseUser) {
          await loadProfile(firebaseUser.uid, user.userType);
        }
      } else {
        setShowErrorMessage(true);
      }
    } catch (err) {
      console.error('Error pausing profile:', err);
      setShowErrorMessage(true);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateClick = () => {
    setShowReactivateConfirm(true);
  };

  const executeReactivate = async () => {
    setLoading(true);
    setShowReactivateConfirm(false);

    try {
      const functions = getFunctions();
      const pauseProfileFunction = httpsCallable(functions, 'pauseProfile');

      const result = await pauseProfileFunction({
        userId: firebaseUser?.uid,
        userType: user?.userType,
        paused: false,
      });

      const data = result.data as { success: boolean; error?: string };

      if (data.success) {
        setIsPaused(false);
        // Refresh profile
        if (user && firebaseUser) {
          await loadProfile(firebaseUser.uid, user.userType);
        }
        done();
      }
    } catch (err) {
      console.error('Error reactivating profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveProfile = () => {
    setShowDeleteConfirm(true);
  };

  const executeDeleteAccount = async () => {
    setDeleteLoading(true);
    setShowDeleteConfirm(false);

    try {
      const functions = getFunctions();
      const deleteUserAccountFn = httpsCallable(functions, 'deleteUserAccount');

      const result = await deleteUserAccountFn({
        confirmDelete: true,
      });

      const data = result.data as { success: boolean; error?: string; message?: string };

      if (data.success) {
        setShowDeletionSuccess(true);
        setDeletionCountdown(REDIRECT_COUNTDOWN_SECONDS);
      } else {
        alert(data.error || 'Failed to delete account. Please try again or contact support.');
      }
    } catch (err) {
      console.error('Error deleting account:', err);
      alert('An error occurred while deleting your account. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (showDeletionSuccess) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100000] p-4">
        <div className="bg-dark-900 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl border border-emerald-500/30">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <svg
                className="w-10 h-10 text-emerald-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4 text-center">
            Account deleted successfully
          </h2>
          <p className="text-gray-300 text-center mb-2">
            Your account has been deleted successfully.
          </p>
          <p className="text-gray-300 text-center mb-2">
            You&apos;ll be redirected in{' '}
            <span className="font-mono text-white font-semibold">{deletionCountdown}</span> second
            {deletionCountdown !== 1 ? 's' : ''}…
          </p>
          <p className="text-gray-500 text-sm text-center">You are now logged out.</p>
        </div>
      </div>
    );
  }

  if (isPaused === null) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // If already paused, show reactivate option
  if (isPaused) {
    return (
      <div className="w-full md:max-w-xl md:mx-auto mt-4 md:mt-8 py-6 px-5 bg-[#1e1e1e] rounded-[20px] shadow-2xl border border-gray-800">
        <div className="flex flex-col items-center">
          <div className="w-14 md:w-16 mx-auto mb-4">
            <img className="w-full opacity-80" src={prairiSettingIcon} alt="PRAIRI Settings icon" />
          </div>
          <h1 className="font-bold text-center w-full text-white text-lg mb-2">
            Your profile is paused
          </h1>
          <p className="text-gray-400 text-center text-sm mb-6 max-w-sm">
            Your profile is currently paused and won&apos;t appear in matches.
          </p>
          <button
            onClick={handleReactivateClick}
            disabled={loading}
            className="px-5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Processing...' : 'Reactivate your profile'}
          </button>
          <button onClick={done} className="mt-4 text-gray-400 hover:text-gray-300 underline">
            Back to Settings
          </button>
        </div>

        {/* Reactivate Confirmation Modal */}
        {showReactivateConfirm && (
          <div
            className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[100000] px-4"
            onClick={() => setShowReactivateConfirm(false)}
          >
            <div
              className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-center mb-4">
                <img src={prairiLogo} alt="PRAIRI Logo" className="w-16 h-16" />
              </div>
              <h3 className="text-xl font-bold text-white text-center mb-4">Reactivate Profile?</h3>
              <p className="text-gray-300 text-center mb-6">
                Are you sure you want to reactivate your profile? This will make your profile
                visible to matches again.
              </p>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => setShowReactivateConfirm(false)}
                  className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeReactivate}
                  className="px-6 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg transition-colors font-medium"
                >
                  Yes, Reactivate
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="w-full md:max-w-xl md:mx-auto mt-4 md:mt-8 py-6 px-5 bg-[#1e1e1e] rounded-[20px] shadow-2xl border border-gray-800">
        <div className="flex flex-col items-center w-full my-2 text-white text-base">
          {user?.userType === 'startup' ? (
            <p className="mb-4 text-center text-base text-white">
              Oh, are you no longer seeking funding?
            </p>
          ) : null}
          {/* Settings Icon */}
          <div className="w-14 md:w-16 mx-auto mb-4">
            <img className="w-full opacity-80" src={prairiSettingIcon} alt="PRAIRI Settings icon" />
          </div>

          {/* Title */}
          <h1 className="font-bold text-center w-full text-white text-lg mb-4">
            Pause your match profile
          </h1>
          <h2 className="text-left w-full text-gray-400 text-xs uppercase tracking-wider mb-3">
            Reason for pausing:
          </h2>

          <div className="w-full">
            {reasons.map(reasonOption => (
              <div key={reasonOption.value} className="mb-3">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="pause-reason"
                    value={reasonOption.value}
                    checked={reason === reasonOption.value}
                    onChange={e => {
                      setReason(e.target.value);
                      setShowErrorMessage(false);
                      if (e.target.value !== 'other') {
                        setOtherReason('');
                      }
                    }}
                    className="mr-3 w-3.5 h-3.5 text-emerald-500 border-gray-600 focus:ring-emerald-500"
                  />
                  <span className="text-white text-sm">{reasonOption.label}</span>
                </label>
                {reasonOption.value === 'other' && reason === 'other' && (
                  <input
                    type="text"
                    value={otherReason}
                    onChange={e => {
                      setOtherReason(e.target.value);
                      setShowErrorMessage(false);
                    }}
                    placeholder="Please specify..."
                    className="mt-2 ml-7 w-full px-3 py-2 rounded-md border-2 border-yellow-500 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                )}
              </div>
            ))}
          </div>

          {showErrorMessage && (
            <p className="text-red-400 text-sm mt-2 w-full">
              Please select a reason for pausing
              {reason === 'other' && !otherReason.trim() && ' and provide details'}.
            </p>
          )}

          {/* Buttons */}
          <div className="my-5 flex items-center justify-between w-full gap-3">
            <button
              onClick={handlePause}
              disabled={loading}
              className="px-4 py-2 border-2 border-yellow-500 text-yellow-500 text-sm font-medium rounded-lg hover:bg-yellow-500 hover:text-black transition-colors disabled:opacity-50 flex-1"
            >
              {loading ? 'Processing...' : 'Pause Data Profile'}
            </button>
            <button
              onClick={done}
              className="px-3 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors"
            >
              Back
            </button>
          </div>

          {/* Remove Profile Link */}
          <button
            onClick={() => navigate('/remove')}
            className="mt-2 text-gray-400 hover:text-gray-300 underline text-xs"
          >
            Remove Profile
          </button>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000]"
          onClick={() => {
            setShowSuccessModal(false);
            done();
          }}
        >
          <div
            className="bg-gray-800 rounded-lg p-6 max-w-md mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4">
              <img src={prairiLogo} alt="PRAIRI Logo" className="w-16 h-16" />
            </div>
            <p className="text-white text-center mb-2">
              Your profile has been paused successfully.
            </p>
            <p className="text-gray-300 text-center text-sm mb-6">
              We&apos;ll check back with you from time to time to see when you&apos;re ready to come
              back!
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  done();
                }}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                OK
              </button>
              <button
                onClick={executeReactivate}
                className="px-6 py-2 text-gray-300 hover:text-white underline"
              >
                Undo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 px-4"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="bg-gray-800 rounded-lg p-8 max-w-md w-full border border-red-900 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4 text-red-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-white text-center mb-4">
              Delete Account Permanently?
            </h3>
            <p className="text-gray-300 text-center mb-6">
              This action is <span className="text-red-400 font-bold uppercase">permanent</span> and
              cannot be undone. All your profile data, match history, and subscription info will be
              permanently deleted per GDPR requirements.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={executeDeleteAccount}
                disabled={deleteLoading}
                className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-bold uppercase tracking-wider"
              >
                {deleteLoading ? 'Deleting Account...' : 'Yes, Delete My Account'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="w-full py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PauseProfilePage;
